<script setup>
import { computed } from 'vue'
import PreferredIcon from '@core/components/PreferredIcon.vue'
import { getRestaurantImageUrl } from '@core/utils/restaurantImage'

const props = defineProps({
  restaurant: { type: Object, required: true },
  fallbackName: { type: String, default: '' },
})

const displayName = computed(() => props.restaurant?.name || props.fallbackName)

const imageUrl = computed(() => getRestaurantImageUrl(props.restaurant))

const initialLetter = computed(() => {
  const n = displayName.value || ''

  return n.charAt(0).toUpperCase() || '?'
})

const initialBgColor = computed(() => {
  const colors = ['#3F998F', '#E8927C', '#7C9EB2', '#B8A9C9', '#8DB580', '#D4A574', '#6B8E9B', '#C4847C']
  const name = displayName.value || ''
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)

  return colors[Math.abs(hash) % colors.length]
})

const isPreferred = computed(() => props.restaurant?.status === 'Preferred')

const profileId = computed(() => props.restaurant?.profileId)
</script>

<template>
  <RouterLink
    :to="profileId ? `/r/${profileId}` : '#'"
    class="ra-restaurant-card"
  >
    <div class="ra-restaurant-card__image">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="displayName"
        class="ra-restaurant-card__img"
        loading="lazy"
      >
      <div
        v-else
        class="ra-restaurant-card__initial"
        :style="{ backgroundColor: initialBgColor }"
      >
        {{ initialLetter }}
      </div>
    </div>

    <div class="ra-restaurant-card__body">
      <div class="ra-restaurant-card__title-row">
        <PreferredIcon
          v-if="isPreferred"
          :size="16"
        />
        <span
          class="ra-restaurant-card__name"
          :class="{ 'ra-restaurant-card__name--preferred': isPreferred }"
        >
          {{ displayName }}
        </span>
      </div>
    </div>

    <VIcon
      icon="mdi-chevron-right"
      size="20"
      class="ra-restaurant-card__chevron"
    />
  </RouterLink>
</template>

<style lang="scss" scoped>
.ra-restaurant-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--ds-border-default, #e0e0e0);
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: var(--ds-brand-primary-dark, #3F998F);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: scale(0.99);
  }
}

.ra-restaurant-card__image {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  background: rgb(248, 249, 250);
}

.ra-restaurant-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ra-restaurant-card__initial {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: var(--ds-font-body);
  font-size: 26px;
  font-weight: 600;
  opacity: 0.92;
  user-select: none;
}

.ra-restaurant-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ra-restaurant-card__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.ra-restaurant-card__name {
  font-family: var(--ds-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--ds-text-strong, #050000);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.ra-restaurant-card__name--preferred {
  color: var(--ds-brand-primary-dark, #3F998F);
}

.ra-restaurant-card__chevron {
  flex-shrink: 0;
  color: var(--ds-text-muted, #999);
}

@media (max-width: 600px) {
  .ra-restaurant-card {
    padding: 10px;
    gap: 10px;
  }

  .ra-restaurant-card__image {
    width: 56px;
    height: 56px;
  }

  .ra-restaurant-card__name {
    font-size: 15px;
  }
}
</style>
